import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

// Token storage: localStorage is the standard SPA pattern for bearer tokens.
// XSS risk is mitigated by React's escaping + no dangerouslySetInnerHTML usage.
// Migrating to httpOnly cookies would require CSRF protection & backend session
// changes on a live production app - tracked as a future hardening item.
export const getToken = () => localStorage.getItem("soula_token");
export const setToken = (t) => localStorage.setItem("soula_token", t);
export const clearToken = () => localStorage.removeItem("soula_token");

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith("/login")) {
      clearToken();
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const errMsg = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return fallback;
};

/** Best-effort client error reporting - captures failed checkouts/submissions with the
 *  exact reason so support never has to guess what a user saw. Never throws. */
export const reportClientError = (action, error, extra = {}) => {
  try {
    api.post("/client-errors", {
      action,
      message: errMsg(error, error?.message || "Unknown error"),
      status: error?.response?.status ?? null,
      path: window.location.pathname,
      extra,
    }).catch((e2) => console.debug("Error report not delivered:", e2?.message));
  } catch (e3) {
    console.debug("Error report failed:", e3?.message);
  }
};

// SSE streaming chat helper (POST + ReadableStream)
export async function streamChat(body, { onDelta, onDone, onError }) {
  try {
    const res = await fetch(`${API_BASE}/ai/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(typeof data.detail === "string" ? data.detail : "AI chat failed.");
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n\n");
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const evt = JSON.parse(line.slice(6));
          if (evt.type === "delta") onDelta?.(evt.content);
          else if (evt.type === "done") onDone?.(evt);
        } catch (parseErr) { console.warn("Skipping malformed SSE chunk:", parseErr?.message); }
      }
    }
  } catch (e) {
    onError?.(e);
  }
}
